import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { Shield, Clock, ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useMFAVerify } from '@/hooks/use-auth-api'

const totpSchema = z.object({
  code: z.string()
    .length(6, 'TOTP code must be 6 digits')
    .regex(/^\d+$/, 'Code must contain only digits'),
})

const backupCodeSchema = z.object({
  code: z.string()
    .regex(/^([a-z0-9]{8}|[a-z0-9]{4}-[a-z0-9]{4})$/i, 'Invalid backup code format'),
})

export function MFAChallengePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const verifyMutation = useMFAVerify()

  const [useBackupCode, setUseBackupCode] = useState(false)
  const [timeLeft, setTimeLeft] = useState(30)
  const inputRef = useRef<HTMLInputElement>(null)

  // Get email from location state
  const email = location.state?.email as string | undefined
  const mfaChallengeToken = location.state?.mfa_challenge_token as string | undefined

  // Redirect to login if no email provided
  useEffect(() => {
    if (!email || !mfaChallengeToken) {
      toast.error('Session expired. Please log in again.')
      navigate('/login', { replace: true })
    }
  }, [email, mfaChallengeToken, navigate])

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // TOTP countdown timer
  useEffect(() => {
    if (useBackupCode) return // Don't run timer for backup codes

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          return 30 // Reset to 30 seconds
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [useBackupCode])

  const currentSchema = useBackupCode ? backupCodeSchema : totpSchema

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<{ code: string }>({
    resolver: zodResolver(currentSchema),
    mode: 'onChange',
  })

  // Watch the code input for auto-submit
  const codeValue = watch('code')

  // Auto-submit when code reaches expected length
  useEffect(() => {
    const expectedLength = useBackupCode && codeValue?.includes('-') ? 9 : useBackupCode ? 8 : 6
    if (codeValue && codeValue.length === expectedLength) {
      // Validate the code format before submitting
      const result = currentSchema.safeParse({ code: codeValue })
      if (result.success) {
        handleSubmit(onSubmit)()
      }
    }
  }, [codeValue, useBackupCode, currentSchema, handleSubmit])

  const onSubmit = async (data: { code: string }) => {
    if (!email || !mfaChallengeToken) {
      toast.error('Session expired. Please log in again.')
      navigate('/login', { replace: true })
      return
    }

    try {
      await verifyMutation.mutateAsync({
        email,
        code: data.code,
        mfa_challenge_token: mfaChallengeToken,
        backup_code: useBackupCode,
      })

      toast.success('Welcome back!')
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      toast.error(error.message || 'Verification failed. Please try again.')
      reset() // Clear the form on error
    }
  }

  const toggleCodeType = () => {
    setUseBackupCode(!useBackupCode)
    reset() // Clear the form when toggling
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  if (!email) {
    return null // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Enter Verification Code
          </CardTitle>
          <CardDescription className="text-center">
            {useBackupCode
              ? 'Enter one of your 8-character backup codes'
              : 'Enter the 6-digit code from your authenticator app'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm font-medium">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </label>
              <Input
                id="code"
                type="text"
                placeholder={useBackupCode ? '12345678' : '000000'}
                autoComplete="off"
                maxLength={useBackupCode ? 9 : 6}
                className="text-center text-2xl tracking-wider font-mono"
                error={errors.code?.message}
                {...register('code')}
                ref={(e) => {
                  register('code').ref(e)
                  // @ts-expect-error - ref assignment
                  inputRef.current = e
                }}
              />
              {errors.code && (
                <p className="text-sm text-destructive mt-1">
                  {errors.code.message}
                </p>
              )}
            </div>

            {/* TOTP timer */}
            {!useBackupCode && (
              <div className="flex items-center justify-center text-sm text-muted-foreground">
                <Clock className="w-4 h-4 mr-2" />
                <span>
                  Code refreshes in <span className="font-medium text-foreground">{timeLeft}s</span>
                </span>
              </div>
            )}

            {/* Toggle between TOTP and backup code */}
            <div className="text-center">
              <button
                type="button"
                onClick={toggleCodeType}
                className="text-sm text-primary hover:underline"
              >
                {useBackupCode
                  ? 'Use authenticator app instead'
                  : 'Use backup code instead'}
              </button>
            </div>

            {/* Manual submit button */}
            <Button
              type="submit"
              className="w-full"
              loading={verifyMutation.isPending}
            >
              Verify
            </Button>
          </form>

          {/* Help text */}
          {!useBackupCode && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Can't access your authenticator app?</span>
                <br />
                Use one of your backup codes that were provided during MFA setup.
              </p>
            </div>
          )}

          {useBackupCode && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Important:</span>
                {' '}Each backup code can only be used once. Make sure to save your remaining codes securely.
              </p>
            </div>
          )}

          {/* Back to login */}
          <div className="mt-6">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </div>

          {/* Lost access link */}
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Lost access to your authenticator?
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
